import { useMemo } from "react";
import { CourtGridData, CourtGridCourt } from "../../../models/CourtGridData";

interface UseCourtFilteringProps {
  courtGridData: CourtGridData | null;
  selectedCourts: string[];
  isMobile: boolean;
  currentCourtIndex: number;
  exerciseName?: string;
}

// Funkcja sortująca korty według exerciseName
const sortCourtsByExerciseName = (courts: CourtGridCourt[], exerciseName?: string): CourtGridCourt[] => {
  if (!exerciseName) return courts;

  // Utwórz mapę z pozycjami kortów z exerciseName
  const courtOrderMap = new Map<string, number>();
  exerciseName
    .split(";")
    .map((name) => name.trim())
    .forEach((courtName, index) => {
      courtOrderMap.set(courtName, index);
    });

  // Sortuj korty na podstawie kolejności w mapie
  return [...courts].sort((a, b) => {
    const orderA = courtOrderMap.has(a.courtName) ? courtOrderMap.get(a.courtName)! : 999;
    const orderB = courtOrderMap.has(b.courtName) ? courtOrderMap.get(b.courtName)! : 999;
    return orderA - orderB;
  });
};

export default function useCourtFiltering({
  courtGridData,
  selectedCourts,
  isMobile,
  currentCourtIndex,
  exerciseName,
}: UseCourtFilteringProps) {
  const filteredCourts = useMemo(() => {
    if (!courtGridData?.courts) return [];

    let courts = courtGridData.courts;

    if (selectedCourts.length > 0) {
      // Zachowaj kolejność z selectedCourts, ale potem posortuj według exerciseName
      courts = selectedCourts
        .map((courtName) => courtGridData.courts.find((court) => court.courtName === courtName))
        .filter((court): court is NonNullable<typeof court> => court !== undefined);
    }

    // Zawsze sortuj według exerciseName
    return sortCourtsByExerciseName(courts, exerciseName);
  }, [courtGridData?.courts, selectedCourts, exerciseName]);

  const displayedCourts = useMemo(() => {
    if (!isMobile || filteredCourts.length === 0) return filteredCourts;

    const court = filteredCourts[currentCourtIndex];
    return court ? [court] : [];
  }, [filteredCourts, isMobile, currentCourtIndex]);

  const uniqueHours = useMemo(() => {
    return Array.from(new Set(filteredCourts.flatMap((court) => court.timeSlots.map((slot) => slot.startTime)))).sort();
  }, [filteredCourts]);

  const findSlot = (courtName: string, startTime: string) =>
    filteredCourts.find((c) => c.courtName === courtName)?.timeSlots.find((slot) => slot.startTime === startTime);

  return {
    filteredCourts,
    displayedCourts,
    uniqueHours,
    findSlot,
  };
}
